import { useEffect, useCallback, useState, useRef } from 'react';
import Terraformer from 'terraformer-arcgis-parser';
import Settings from 'components/Map/settings';

// Helper function to calculate area from geometry extent
const calculateArea = (extent: any): number => {
  if (!extent) return 0;
  const width = extent.xmax - extent.xmin;
  const height = extent.ymax - extent.ymin;
  return (width * height) / 1000000; // Convert to km²
};

export const useLualExtent = (enquiryShape: any, setLualExtent: Function) => {
  const [lualZoomLevel, setLualZoomLevel] = useState<number | null>(null);
  const [isLualMapLoaded, setIsLualMapLoaded] = useState<boolean>(false);
  const [lualMapError, setLualMapError] = useState<string | null>(null);
  const mapRef = useRef<any>(null);
  const calculateExtentFromShape = useCallback((shape: any) => {
    if (!shape) return null;

    try {
      // Convert the shape to ArcGIS geometry
      const geometry: any = Terraformer.convert(shape);

      // Calculate bounds
      let xmin = Infinity,
        ymin = Infinity,
        xmax = -Infinity,
        ymax = -Infinity;

      if (geometry.rings) {
        // Polygon geometry
        geometry.rings.forEach((ring: number[][]) => {
          ring.forEach(([x, y]: number[]) => {
            xmin = Math.min(xmin, x);
            ymin = Math.min(ymin, y);
            xmax = Math.max(xmax, x);
            ymax = Math.max(ymax, y);
          });
        });
      }

      // Create extent object
      const extent = {
        xmin,
        ymin,
        xmax,
        ymax,
        spatialReference: { wkid: 4326 } // Geographic coordinate system
      };

      // Calculate buffer size (similar to MapView logic)
      const area = calculateArea(extent);
      const bufferSize = Math.sqrt(area) * 0.2;
      const bufferSizeInMeters = bufferSize * 1000;

      // Return extent with buffer
      return {
        xmin: extent.xmin - bufferSizeInMeters,
        ymin: extent.ymin - bufferSizeInMeters,
        xmax: extent.xmax + bufferSizeInMeters,
        ymax: extent.ymax + bufferSizeInMeters,
        spatialReference: extent.spatialReference
      };
    } catch (error) {
      return null;
    }
  }, []);

  // Function to cleanup invisible map and stop workers
  const cleanupInvisibleMap = useCallback(() => {
    if (mapRef.current) {
      try {
        // Destroy the map view to stop all workers
        if (mapRef.current.view) {
          mapRef.current.view.destroy();
        }
        // Remove the hidden container from DOM
        if (mapRef.current.container && mapRef.current.container.parentNode) {
          mapRef.current.container.parentNode.removeChild(
            mapRef.current.container
          );
        }
        // Clear the reference
        mapRef.current = null;
      } catch (error) {
        console.warn('Warning: Error cleaning up invisible map:', error);
      }
    }
  }, []);

  const handleLualMapZoomChange = useCallback(
    (zoomLevel: number) => {
      // Add +3 to match the visible LUAL map zoom level
      const adjustedZoomLevel = zoomLevel + 3;
      setLualZoomLevel(adjustedZoomLevel);
      setIsLualMapLoaded(true);
      // Stop the invisible map after getting zoom level
      cleanupInvisibleMap();
    },
    [cleanupInvisibleMap]
  );

  const loadLualMapInBackground = useCallback(
    async (extent: any) => {
      if (!extent || isLualMapLoaded) return;

      try {
        // Dynamically import the required modules
        const { loadModules } = await import('esri-loader');
        const [WebMap, MapView, Config, Extent] = await loadModules([
          'esri/WebMap',
          'esri/views/MapView',
          'esri/config',
          'esri/geometry/Extent'
        ]);

        // Always set API key - interceptors will replace it with session token for basemap requests
        Config.apiKey = process.env.REACT_APP_ESRI_API_KEY;

        // Create a hidden container for the map
        const hiddenContainer = document.createElement('div');
        hiddenContainer.style.position = 'absolute';
        hiddenContainer.style.top = '-9999px';
        hiddenContainer.style.left = '-9999px';
        hiddenContainer.style.width = '100px';
        hiddenContainer.style.height = '100px';
        hiddenContainer.style.zIndex = '-1';
        document.body.appendChild(hiddenContainer);

        // Create the web map
        const webmap = new WebMap({
          portalItem: {
            id: '2ef48ad879b6488e9239298dd45f71ed'
          }
        });

        // Create the map view
        const view = new MapView({
          container: hiddenContainer,
          map: webmap,
          extent: extent,
          constraints: {
            maxZoom: 22,
            geometry: new Extent(Settings.AUS_Boundary_Constraint),
            minScale: Settings.mapMinScale
          }
        });

        // Store reference to the map
        mapRef.current = { view, webmap, container: hiddenContainer };

        // Wait for the view to be ready and capture zoom level
        view.when(() => {
          // Only capture initial zoom level, don't watch for changes
          handleLualMapZoomChange(view.zoom);
          // Note: We don't watch for zoom changes since we only need the initial calculation
          // The map will be destroyed immediately after getting the zoom level
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        setLualMapError(`Map loading failed: ${errorMessage}`);
        // Set a fallback zoom level to prevent undefined issues
        setLualZoomLevel(15); // Default zoom level as fallback
        setIsLualMapLoaded(true);
      }
    },
    [isLualMapLoaded, handleLualMapZoomChange]
  );

  useEffect(() => {
    if (enquiryShape && setLualExtent) {
      const extent = calculateExtentFromShape(enquiryShape);
      if (extent) {
        setLualExtent(extent);
        // Load the map immediately when we have the extent
        loadLualMapInBackground(extent);
      }
    }
  }, [
    enquiryShape,
    setLualExtent,
    calculateExtentFromShape,
    loadLualMapInBackground
  ]);

  // Cleanup function to remove the hidden map when component unmounts
  useEffect(() => {
    return () => {
      // Use our optimized cleanup function
      cleanupInvisibleMap();
    };
  }, [cleanupInvisibleMap]);

  return {
    lualZoomLevel,
    isLualMapLoaded,
    lualMapError,
    handleLualMapZoomChange
  };
};
